import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  BanIcon,
  CheckIcon,
  CircleAlertIcon,
  EqualNotIcon,
  LoaderCircleIcon,
  MinusIcon,
  PlusIcon,
  TriangleAlertIcon,
  WifiOffIcon,
} from "lucide-react";
import type { WeekPlan } from "@/types/meal-plan-state";
import { useAuth } from "@/context/auth-context";
import { useFriends } from "@/context/friends-context";
import { fetchFriendPlan, fetchWeekSnapshot, saveWeekVersioned, type WeekScope } from "@/lib/api";
import { loadComidasSharePrefs, saveComidasSharePrefs, type ComidasSharePrefs } from "@/lib/comidas-share-prefs";
import { includedFriendIds, rememberPlannedFor, setIncluded } from "@/lib/compras-friends";
import { missingDayActions, type FriendChipKind } from "@/lib/comidas-share";
import { FriendWeekWriter, type FriendWriterState } from "@/lib/friend-week-writer";
import { isSessionEnding, registerPendingSaveFlush, reportLostFriendEdit, trackLeavingFlush } from "@/lib/pending-saves";
import { useOnline } from "@/lib/use-online";
import { getTodayDate } from "@/lib/week-utils";
import { focusRing } from "@/lib/format";
import { cn } from "@/lib/utils";
import { initialOf } from "@/components/person-avatar";

export interface ShareFriend {
  friendId: number;
  username: string;
}

export interface ShareWriterView {
  friend: ShareFriend;
  writer: FriendWeekWriter;
  state: FriendWriterState;
}

export interface ComidasShare {
  /** Accepted friends, the chips of the bar. */
  friends: ShareFriend[];
  isOn: (friendId: number) => boolean;
  toggle: (friendId: number) => void;
  /** One writer per friend switched on, for the shown week. */
  writers: ShareWriterView[];
}

function writerFor(friend: ShareFriend, weekId: string): FriendWeekWriter {
  const scope: WeekScope = { kind: "friend", friendId: friend.friendId };
  return new FriendWeekWriter(friend.friendId, friend.username, weekId, {
    fetchContext: () => fetchFriendPlan(friend.friendId),
    fetchSnapshot: (id) => fetchWeekSnapshot(scope, id),
    save: (id, plan, baseVersion, kind) => saveWeekVersioned(scope, id, plan, baseVersion, kind),
    today: getTodayDate,
  });
}

/** Leaving a writer (week change, switched off, leaving Comidas): its pending writes still go. */
function retire(writer: FriendWeekWriter): void {
  if (!isSessionEnding()) {
    trackLeavingFlush(writer.flush()).catch((error: unknown) => {
      console.error(`Unsaved changes for ${writer.username} were left behind`, error);
      // Same toast as leaving friend mode with an unsaved edit; a 403 already said why.
      if (writer.getState().saveStatus !== "forbidden") reportLostFriendEdit(writer.username);
    });
  }
  writer.dispose();
}

/**
 * "También para" state for my Comidas: which accepted friends are on
 * (remembered per viewer, `comidas-share-prefs`), and a `FriendWeekWriter`
 * per friend on for `weekId` (none when `enabled` is false: friend mode).
 */
export function useComidasShare(weekId: string, enabled: boolean): ComidasShare {
  const { user } = useAuth();
  const { friends: allFriends } = useFriends();
  const userId = user?.id ?? null;
  const [prefs, setPrefs] = useState<ComidasSharePrefs>(() => (userId ? loadComidasSharePrefs(userId) : {}));

  const friends = useMemo(
    () =>
      allFriends
        .filter((f) => f.status === "accepted")
        .map((f) => ({ friendId: f.friend_id, username: f.username }))
        .sort((a, b) => a.username.localeCompare(b.username)),
    [allFriends],
  );
  const onIds = useMemo(() => includedFriendIds(prefs, friends.map((f) => f.friendId)), [prefs, friends]);

  const toggle = useCallback(
    (friendId: number) => {
      if (!userId) return;
      const next = setIncluded(prefs, friendId, !onIds.has(friendId));
      setPrefs(next);
      saveComidasSharePrefs(userId, next);
      // Planning for them means buying for them: Compras includes them too, unless I said otherwise there.
      if (next[friendId]) rememberPlannedFor(userId, friendId);
    },
    [prefs, onIds, userId],
  );

  // Writers, keyed "friendId:weekId", created and retired as friends and weeks change.
  const writersRef = useRef(new Map<string, FriendWeekWriter>());
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const active = useMemo(
    () => (enabled && weekId ? friends.filter((f) => onIds.has(f.friendId)) : []),
    [enabled, weekId, friends, onIds],
  );
  const activeKey = active.map((f) => `${f.friendId}:${weekId}`).join(",");

  useEffect(() => {
    const wanted = new Set(activeKey ? activeKey.split(",") : []);
    const writers = writersRef.current;
    for (const [key, writer] of writers) {
      if (wanted.has(key)) continue;
      writers.delete(key);
      retire(writer);
    }
    for (const friend of active) {
      const key = `${friend.friendId}:${weekId}`;
      if (writers.has(key)) continue;
      const writer = writerFor(friend, weekId);
      writers.set(key, writer);
      writer.subscribe(rerender);
      void writer.load();
    }
    rerender();
    // `active` is read through `activeKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  // Logout waits for their writes too.
  useEffect(() => {
    const writers = writersRef.current;
    const unregister = registerPendingSaveFlush(async () => {
      const results = await Promise.allSettled([...writers.values()].map((w) => w.flush()));
      const failed = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
      if (failed) throw failed.reason;
    });
    return () => {
      unregister();
      for (const writer of writers.values()) retire(writer);
      writers.clear();
    };
  }, []);

  // Back on the tab: their changes; back online: what didn't save.
  useEffect(() => {
    const each = (fn: (w: FriendWeekWriter) => void) => () => {
      for (const writer of writersRef.current.values()) fn(writer);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") each((w) => void w.refetch())();
    };
    const onOnline = each((w) => void w.retry());
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  const writers: ShareWriterView[] = active.flatMap((friend) => {
    const writer = writersRef.current.get(`${friend.friendId}:${weekId}`);
    return writer ? [{ friend, writer, state: writer.getState() }] : [];
  });

  return { friends, isOn: (id) => onIds.has(id), toggle, writers };
}

// ── The bar ──

function dias(n: number): string {
  return n === 1 ? "1 día" : `${n} días`;
}

/**
 * Top of my Comidas: "También para [david] [mateo]", plus at most one line
 * about the friends' weeks (offline, a failed save, loading, days they lack).
 */
export function ShareBar({
  share,
  myWeek,
  onCopied,
}: {
  share: ComidasShare;
  myWeek: WeekPlan;
  onCopied: (message: string) => void;
}) {
  const online = useOnline();
  if (share.friends.length === 0) return null;

  const line = shareLine(share.writers, myWeek, online);

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 overflow-x-auto px-1" role="group" aria-label="Planificar también para">
        <span className="shrink-0 text-sm text-muted-foreground">También para</span>
        {share.friends.map((friend) => {
          const on = share.isOn(friend.friendId);
          return (
            <button
              key={friend.friendId}
              type="button"
              aria-pressed={on}
              aria-label={`Planificar también para ${friend.username}: ${on ? "activado" : "desactivado"}`}
              onClick={() => share.toggle(friend.friendId)}
              className={cn(
                "flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-sm transition-colors",
                on
                  ? "border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-700 dark:bg-violet-900/60 dark:text-violet-50"
                  : "text-muted-foreground hover:bg-muted",
                focusRing,
              )}
            >
              {on ? <CheckIcon className="size-3.5" /> : <PlusIcon className="size-3.5" />}
              {friend.username}
            </button>
          );
        })}
      </div>
      {line && (
        <p
          className={cn("flex items-center gap-1.5 px-1 text-xs", line.tone === "error" ? "text-destructive" : "text-muted-foreground")}
          role={line.tone === "error" ? "alert" : "status"}
        >
          {line.icon}
          <span className="min-w-0">{line.text}</span>
          {line.action && (
            <button
              type="button"
              onClick={() => {
                const message = line.action!.run();
                if (message) onCopied(message);
              }}
              className={cn("shrink-0 font-medium text-primary underline underline-offset-2", focusRing)}
            >
              {line.action.label}
            </button>
          )}
        </p>
      )}
    </div>
  );
}

interface ShareLine {
  text: string;
  tone: "error" | "muted";
  icon?: React.ReactNode;
  /** `run` returns a toast message, if any. */
  action?: { label: string; run: () => string | null };
}

function shareLine(writers: ShareWriterView[], myWeek: WeekPlan, online: boolean): ShareLine | null {
  const first = (test: (v: ShareWriterView) => boolean) => writers.find(test);
  const names = (test: (v: ShareWriterView) => boolean) =>
    writers.filter(test).map((v) => v.friend.username).join(" y ");

  const offline = (v: ShareWriterView) => !online && v.state.pending;
  if (first(offline)) {
    return { text: `Sin conexión · lo de ${names(offline)} se guardará al volver`, tone: "muted", icon: <WifiOffIcon className="size-3.5 shrink-0" /> };
  }
  const failed = first((v) => v.state.saveStatus === "error");
  if (failed) {
    return {
      text: `No se guardó en la semana de ${failed.friend.username}`,
      tone: "error",
      icon: <CircleAlertIcon className="size-3.5 shrink-0" />,
      action: { label: "Reintentar", run: () => (void failed.writer.retry(), null) },
    };
  }
  const forbidden = first((v) => v.state.saveStatus === "forbidden");
  if (forbidden) {
    return { text: `No tienes permiso para guardar en la semana de ${forbidden.friend.username}.`, tone: "error", icon: <CircleAlertIcon className="size-3.5 shrink-0" /> };
  }
  const loadFailed = first((v) => v.state.load === "error");
  if (loadFailed) {
    return {
      text: `No pudimos abrir la semana de ${loadFailed.friend.username}`,
      tone: "error",
      icon: <CircleAlertIcon className="size-3.5 shrink-0" />,
      action: { label: "Reintentar", run: () => (void loadFailed.writer.retry(), null) },
    };
  }
  const loading = (v: ShareWriterView) => v.state.load === "loading";
  if (first(loading)) {
    return { text: `Abriendo la semana de ${names(loading)}…`, tone: "muted", icon: <LoaderCircleIcon className="size-3.5 shrink-0 animate-spin" /> };
  }
  for (const v of writers) {
    const plan = v.state.plan;
    if (!plan) continue;
    const actions = missingDayActions(myWeek, plan);
    if (actions.length === 0) continue;
    return {
      text: `A ${v.friend.username} le faltan las comidas de ${dias(actions.length)} ·`,
      tone: "muted",
      action: {
        label: "Copiar",
        run: () => {
          // Re-read: the week may have changed since this line rendered.
          const current = v.writer.getState().plan;
          const todo = current ? missingDayActions(myWeek, current) : [];
          for (const action of todo) v.writer.apply(action);
          return todo.length > 0 ? `Copiamos ${dias(todo.length)} a ${v.friend.username}.` : null;
        },
      },
    };
  }
  return null;
}

// ── Per-row chip ──

const CHIP_LABEL: Record<FriendChipKind, string> = {
  ok: "guardado",
  warn: "falta algo en su plan",
  error: "no reconocida en su plan",
  different: "tiene otra comida",
  missing: "sin esta comida",
  unavailable: "no tiene esta comida este día",
};

export function FriendChip({
  username,
  kind,
  saving,
  expanded,
  onToggle,
}: {
  username: string;
  kind: FriendChipKind;
  saving: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const badge = saving ? (
    <LoaderCircleIcon className="size-2.5 animate-spin text-muted-foreground" />
  ) : kind === "ok" ? (
    <CheckIcon className="size-2.5 text-emerald-600 dark:text-emerald-400" strokeWidth={3} />
  ) : kind === "warn" ? (
    <TriangleAlertIcon className="size-2.5 text-amber-600 dark:text-amber-400" strokeWidth={3} />
  ) : kind === "error" ? (
    <CircleAlertIcon className="size-2.5 text-destructive" strokeWidth={3} />
  ) : kind === "different" ? (
    <EqualNotIcon className="size-2.5 text-muted-foreground" strokeWidth={3} />
  ) : kind === "missing" ? (
    <MinusIcon className="size-2.5 text-muted-foreground" strokeWidth={3} />
  ) : (
    <BanIcon className="size-2.5 text-muted-foreground" strokeWidth={3} />
  );
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={`${username}: ${saving ? "guardando" : CHIP_LABEL[kind]}`}
      title={`${username}: ${saving ? "guardando" : CHIP_LABEL[kind]}`}
      className={cn("relative -my-1 grid size-7 shrink-0 place-items-center rounded-full", focusRing)}
    >
      <span
        aria-hidden="true"
        className={cn(
          "grid size-5 place-items-center rounded-full bg-violet-100 text-[10px] font-semibold text-violet-800 dark:bg-violet-900/60 dark:text-violet-100",
          expanded && "ring-2 ring-violet-400",
        )}
      >
        {initialOf(username)}
      </span>
      <span aria-hidden="true" className="absolute bottom-0 right-0 grid size-3.5 place-items-center rounded-full bg-background">
        {badge}
      </span>
    </button>
  );
}
